import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Tooltip,
  Typography,
} from 'antd';
import {
  EyeOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { getConstanciadeInscripcion, getConstanciasdeInscripcion, patchConstanciadeInscripcion } from 'api/recaudacion/constanciasDeInscripcion';
import { ReadyState } from 'react-use-websocket';
import { logger } from 'utils/debug';
import { useSelector } from 'react-redux';
import { getHistorialesDeConstancia, postHistorialDeConstancia } from 'api/recaudacion/historialDeConstanciasDeInscripcion';
import { toMoment } from 'utils/normalizers';
import { getTramite, patchTramite } from 'api/tramites/tramites';
import { patchSpeedway } from 'api/tramites/speedways';

export const connectionStatusMap = {
  [ReadyState.CONNECTING]: 'Conectando...',
  [ReadyState.OPEN]: 'Abierta',
  [ReadyState.CLOSING]: 'Cerrando..',
  [ReadyState.CLOSED]: 'Cerrada',
};

const AsignacionConstancia = ({
  tramite,
  disabled,
  callback,
  constancia: idConstancia,
  sendMessage,
  lastMessage,
  readyState,
  vehiculo,
}) => {
  const ref = useRef();
  const user = useSelector((s) => s.auth.user);
  const formInstance = Form.useFormInstance();
  const folioLeido = Form.useWatch('folioLeido', formInstance);
  const [messageHistory, setMessageHistory] = useState([]);
  const [_loading, setLoading] = useState(false);
  const [intentoGrabacion, setIntentoGrabacion] = useState('0 de 3');
  const [intentoLectura, setIntentoLectura] = useState('0 de 3');
  const loading = !!_loading;
  const niv = vehiculo.numero_de_identificacion_vehicular;

  const getConstancia = async () => {
    const _constancia = await getConstanciadeInscripcion(idConstancia);
    formInstance.setFieldsValue({
      folioLeido: _constancia.folio,
      tagIdLeido: _constancia.identificador,
      niv,
    });
  };

  useEffect(() => {
    if (ref.current && messageHistory.length) {
      ref.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, [messageHistory]);

  useEffect(() => {
    if (disabled) {
      getConstancia();
    } else {
      setTimeout(() => formInstance.setFieldsValue({ niv }));
    }
  }, []);

  const _sendMessage = (msg) => {
    sendMessage(msg);
    setMessageHistory((p) => p.concat({ msg, sent: true, timeStamp: (new Date()).getTime() }));
  };

  const onValidateTagFail = () => {
    setLoading(false);
    message.info('Falló asociación de chip, intentelo de nuevo');
    return false;
  };

  const linkTag = async (folioTag) => {
    setLoading(true);
    const constancias = await getConstanciasdeInscripcion({ folio: folioTag });
    let [constancia] = constancias.results || constancias;
    const GRABADO = 3;
    if (constancia && constancia.estatus_ci !== GRABADO) {
      constancia = await patchConstanciadeInscripcion(constancia.id, {
        estatus_ci: GRABADO,
        vehiculo: vehiculo.id,
      });
    }
    if (!constancia) return onValidateTagFail();
    const params = {
      niv,
      estatus: GRABADO,
      constancia_de_inscripcion: constancia.id,
    };
    const logs = await getHistorialesDeConstancia(params);
    let [log] = logs.results || logs;
    if (!log) {
      log = await postHistorialDeConstancia({
        ...params,
        danio: 0,
        fecha_de_movimiento: toMoment(new Date()),
      });
    }
    if (!log) return onValidateTagFail();
    let _tramite = await getTramite(tramite.id);
    if (!_tramite.constancia_de_inscripcion) {
      _tramite = await patchTramite(tramite.id, { constancia_de_inscripcion: constancia.id });
    }
    if (!_tramite) return onValidateTagFail();
    await callback(constancia.id);
    setLoading(false);
    message.success('Chip asociado correctamente', 3);
    return true;
  };

  const validateLastMessage = async () => {
    if (!lastMessage) return;
    const customToJson = (str) => Object.fromEntries(
      str
        .split(',')
        .map((chunk) => chunk.split(':').map((keyVal) => keyVal.replace(/[{}"]/g, '')?.trim())),
    );
    const toJson = (str) => {
      try {
        return JSON.parse(str);
      } catch {
        if (/"mensaje"/.test(str)) return { mensaje: customToJson(str).mensaje };
        return null;
      }
    };
    const { data: msg, timeStamp } = lastMessage;
    const prevMessage = messageHistory.at(-1)?.msg;

    if (/idUser=/i.test(prevMessage)) {
      setMessageHistory((p) => p.concat({ msg, timeStamp }));
      if (/el dispositivo se encuentra conectado/i.test(msg)) {
        _sendMessage(_loading);
        return;
      }
      setLoading(false);
      return;
    }
    if (/tag detectado, decodificando\.\.\./i.test(prevMessage)
      && !/folio detectado, consultando api\.\.\./i.test(msg)) {
      setLoading(false);
      message.warn('Ocurrió un error al leer el Tag', 5);
      setMessageHistory((p) => p.concat({ msg, timeStamp }));
      return;
    }
    if (/folio detectado, consultando api\.\.\./i.test(prevMessage)) {
      setLoading(false);
      const tagDecodificado = toJson(msg);
      const vinLeido = tagDecodificado?.vinLeido?.trim();
      if (tagDecodificado?.mensaje) message.warn(tagDecodificado.mensaje, 10);
      if (!tagDecodificado) message.warn('Ocurrió un error al leer el Tag', 10);
      if (vinLeido?.toUpperCase() === niv.toUpperCase()) {
        message.info('Se esta asociando su chip, por favor espere', 0);
        await linkTag(tagDecodificado.folioLeido);
      } else if (vinLeido) {
        message.warn('Tag leido ya tiene NIV asignado', 10);
      } else {
        formInstance.setFieldsValue({
          folioLeido: tagDecodificado.folioLeido,
          tagIdLeido: tagDecodificado.tagIdLeido,
        });
      }
      if (tagDecodificado.noIntentos) {
        setIntentoLectura(tagDecodificado?.noIntentos);
      }
      setMessageHistory((p) => p.concat({ msg, timeStamp }));
      return;
    }
    if (/proporciona el vin/i.test(msg)) {
      setMessageHistory((p) => p.concat({ msg, timeStamp }));
      _sendMessage(niv);
      return;
    }
    if (/vin almacenado/i.test(msg)) {
      setMessageHistory((p) => p.concat({ msg, timeStamp }));
      _sendMessage('Validar');
      return;
    }
    if (/escribe el folio del tramite/i.test(msg)) {
      setMessageHistory((p) => p.concat({ msg, timeStamp }));
      _sendMessage(tramite.folio);
      return;
    }
    if (/(grabado correcto)|(finalizado correctamente)/i.test(msg)) {
      setMessageHistory((p) => p.concat({ msg, timeStamp }));
      await callback();
      return;
    }
    const obj = /mensaje/i.test(msg) ? toJson(msg) : null;
    if (obj?.mensaje) {
      if (/no se pudo conectar con componente/i.test(obj.mensaje)) setLoading(false);
      if (/escribir/i.test(obj.mensaje) && obj.intento) setIntentoGrabacion(obj.intento);
      if (/leer|lectura/i.test(obj.mensaje) && obj.noIntentos) setIntentoLectura(obj.noIntentos);

      if (/(error|problema)/i.test(obj.mensaje) && !/18|21/.test(obj.mensaje)) {
        formInstance.resetFields();
        setLoading(false);
        message.warn('Ha ocurrido un error en el proceso, intente Leer para verificar si no se grabó el NIV', 10);
        setIntentoGrabacion('0 de 3');
        setIntentoLectura('0 de 3');
      }

      setMessageHistory((p) => p.concat({ msg: obj.mensaje, timeStamp }));
      return;
    }
    setMessageHistory((p) => p.concat({ msg, timeStamp }));
  };

  useEffect(() => {
    logger('[lastMessage]', lastMessage?.data);
    validateLastMessage();
  }, [lastMessage]);

  const onClickRead = () => {
    message.destroy();
    setMessageHistory([]);
    _sendMessage(`idUser=${user.id}`);
    setLoading('lectura');
  };

  const onClickWrite = () => {
    _sendMessage(`idUser=${user.id}`);
    setLoading('grabar');
  };

  const onClickClear = () => {
    formInstance.resetFields(['folio', 'tag', 'niv_leido']);
    setLoading(false);
    setIntentoGrabacion('0 de 3');
    setIntentoLectura('0 de 3');
  };

  const connectionStatus = connectionStatusMap[readyState];

  const isOpen = readyState === ReadyState.OPEN;

  const restartSpeedway = async () => {
    setLoading(true);
    await patchSpeedway(user.speedway, { estatus: 1 });
    setLoading(false);
  };

  return (
    <>
      <Col xs={24} sm={24} md={8}>
        <Form.Item name="niv" label="NIV">
          <Input disabled />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item name="folioLeido" label="Folio">
          <Input disabled />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item name="tagIdLeido" label="TAG">
          <Input disabled />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item name="vinLeido" label="NIV leído">
          <Input disabled />
        </Form.Item>
      </Col>
      {!disabled && (<Col span={24} />)}
      <Col>
        <Button
          onClick={onClickRead}
          disabled={!isOpen || disabled || !!folioLeido || loading}
          style={{ marginTop: disabled ? 22 : 0 }}
          className="btn-contrast"
        >
          <EyeOutlined />
          {_loading === 'lectura' ? 'Leyendo CI...' : 'Leer CI'}
        </Button>
      </Col>
      {!disabled && (
        <Col>
          <Button
            onClick={onClickWrite}
            disabled={!isOpen || !folioLeido || loading}
            className="btn-contrast"
          >
            <SaveOutlined />
            {_loading === 'grabar' ? 'Grabando CI...' : 'Grabar CI'}
          </Button>
        </Col>
      )}
      <Col>
        <Button type="link" style={{ marginTop: disabled ? 22 : 0 }}>
          <Tooltip style={{ alignSelf: 'center' }} title={`Estatus de conexión a web sockets: ${connectionStatus}`}>
            <QuestionCircleOutlined />
          </Tooltip>
        </Button>
      </Col>
      <Col>
        <Button
          style={{ marginTop: disabled ? 22 : 0 }}
          disabled={disabled || loading || !isOpen}
          onClick={restartSpeedway}
        >
          <ReloadOutlined />
          Reiniciar speedway
        </Button>
      </Col>
      {!disabled && (
        <>
          <Col span={24}>
            <Typography.Text style={{ float: 'right' }}>
              Intento de grabación:
              {' '}
              {intentoGrabacion}
            </Typography.Text>
          </Col>
          <Col span={24}>
            <Typography.Text style={{ float: 'right' }}>
              Intento de lectura:
              {' '}
              {intentoLectura}
            </Typography.Text>
          </Col>
          <Col span={24}>
            <Button
              onClick={onClickClear}
              disabled={intentoGrabacion !== 3 && intentoLectura !== 3}
              style={{ float: 'right' }}
            >
              <ReloadOutlined />
              Limpiar
            </Button>
          </Col>
        </>
      )}
      <Col span={24} style={{ height: 250, overflowY: 'scroll' }} ref={ref}>
        {messageHistory.map((_message, idx) => (
          <div
            key={_message.timeStamp}
            style={{
              width: '100%',
              color: _message.sent ? 'green' : 'blue',
              fontWeight: 'bold',
              borderTop: _message.msg === 'lectura' && idx ? '2px dashed black' : undefined,
              paddingTop: _message.msg === 'lectura' && idx ? 5 : undefined,
            }}
          >
            {!_message.bl && `Mensaje ${_message.sent ? 'enviado' : 'recibido'}: `}
            {_message.msg}
          </div>
        ))}
      </Col>
    </>
  );
};

AsignacionConstancia.propTypes = {
  tramite: PropTypes.shape({
    folio: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
  }).isRequired,
  disabled: PropTypes.bool.isRequired,
  callback: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  lastMessage: PropTypes.shape({ data: PropTypes.string, timeStamp: PropTypes.number }),
  constancia: PropTypes.number,
  readyState: PropTypes.number,
  vehiculo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    numero_de_identificacion_vehicular: PropTypes.number.isRequired,
  }).isRequired,
};

AsignacionConstancia.defaultProps = {
  constancia: null,
  lastMessage: null,
  readyState: ReadyState.UNINSTANTIATED,
};

export default AsignacionConstancia;
